import { useState } from 'react';
import { RequestFailed } from '../api/client';
import type { Chat, Person } from '../api/types';
import { PersonSearch } from './PersonSearch';

interface Props {
  chat: Chat;
  /** Те, с кем уже есть переписка. Общего списка участников нет by design. */
  contacts: Person[];
  onSearch: (query: string) => Promise<Person[]>;
  onAdd: (chatId: string, memberIds: string[]) => Promise<void>;
  onClose: () => void;
}

/**
 * Добавление людей в существующую группу.
 *
 * Сервер это умел с самого начала, а в интерфейсе способа не было: группу
 * создавали с участниками — и всё, позвать кого-то ещё было нельзя.
 */
export function AddMembersPanel({ chat, contacts, onSearch, onAdd, onClose }: Props) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  /** Найденные поиском — их нет в адресной книге, но добавить можно. */
  const [extra, setExtra] = useState<Person[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const alreadyIn = new Set(chat.members.map((member) => member.userId));
  const candidates = [...contacts, ...extra.filter((p) => !contacts.some((c) => c.id === p.id))]
    .filter((person) => !alreadyIn.has(person.id));

  function toggle(userId: string) {
    setSelected((previous) => {
      const next = new Set(previous);
      if (next.has(userId)) next.delete(userId); else next.add(userId);
      return next;
    });
  }

  function addFound(person: Person) {
    if (alreadyIn.has(person.id)) {
      setError(`${person.displayName} уже в группе`);
      return;
    }
    setExtra((previous) => previous.some((p) => p.id === person.id) ? previous : [...previous, person]);
    setSelected((previous) => new Set(previous).add(person.id));
  }

  async function submit() {
    setError(null);
    setBusy(true);
    try {
      await onAdd(chat.id, [...selected]);
      onClose();
    } catch (e) {
      setError(e instanceof RequestFailed ? e.error.message : 'Не удалось добавить');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="panel">
      <div className="section-title" style={{ padding: '2px 0 6px' }}>
        Кого добавить в «{chat.title}»
      </div>

      <div className="panel-list">
        {candidates.length === 0 && (
          <p className="panel-hint">
            Все знакомые уже в группе. Найдите человека поиском ниже.
          </p>
        )}
        {candidates.map((person) => (
          <label className="check" key={person.id}>
            <input
              type="checkbox"
              checked={selected.has(person.id)}
              onChange={() => toggle(person.id)}
            />
            <span>{person.displayName}</span>
          </label>
        ))}
      </div>

      <div className="panel-actions">
        <button type="button" disabled={busy || selected.size === 0} onClick={() => void submit()}>
          {busy ? 'Добавляем…' : `Добавить (${selected.size})`}
        </button>
        <button type="button" className="ghost" onClick={onClose}>Отмена</button>
      </div>

      {error && <p className="error">{error}</p>}

      <div className="panel-search">
        <div className="section-title" style={{ padding: '10px 0 2px' }}>Найти по имени</div>
        <PersonSearch onSearch={onSearch} onPick={addFound} actionLabel="выбрать" />
      </div>
    </div>
  );
}
